import type { ReactNode } from "react";
import type { MarkdownPreviewProps } from "@uiw/react-markdown-preview";
import { LinkMultipleFilled } from "@fluentui/react-icons";
import styles from "../../MarkdownContent.module.scss";

export interface IHeadingItem {
  id: string;
  title: string;
  level: number;
}

interface IHeadingRenderersProps {
  t: (key: string) => string;
  onCopyLink?: (sectionId: string) => void;
  copiedSectionId?: string | null;
}

function extractTextFromChildren(children: React.ReactNode): string {
  if (children === null || children === undefined) {
    return "";
  }
  if (typeof children === "string") {
    return children;
  }
  if (typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("");
  }
  if (typeof children === "object" && children !== null && "props" in children) {
    return extractTextFromChildren((children as React.ReactElement).props.children);
  }
  return "";
}

export function generateHeadingSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

function extractHeadingText(headingLine: string): string {
  const textPart = headingLine.replace(/^#+\s*/, "");

  return textPart
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(\*|_)(.+?)\1/g, "$2")
    .replace(/`(.+?)`/g, "$1")
    .replace(/\[(.+?)\]\(.+?\)/g, "$1")
    .replace(/~~(.+?)~~/g, "$1")
    .trim();
}

function extractHtmlTagText(html: string): string {
  return html.replace(/<[^>]+>/g, "").trim();
}

export function extractH1Headings(content: string): IHeadingItem[] {
  if (!content || typeof content !== "string") {
    return [];
  }

  interface IHeadingWithPosition {
    title: string;
    position: number;
  }

  const headingsWithPosition: IHeadingWithPosition[] = [];
  const seenTitles = new Set<string>();

  const markdownH1Regex = /^#(?!#)\s+(.+)$/gm;
  let match: RegExpExecArray | null;
  while ((match = markdownH1Regex.exec(content)) !== null) {
    const title = extractHeadingText(match[0]);
    if (title) {
      headingsWithPosition.push({ title, position: match.index });
    }
  }

  const htmlH1Regex = /<h1[^>]*>([\s\S]*?)<\/h1>/gi;
  while ((match = htmlH1Regex.exec(content)) !== null) {
    const title = extractHtmlTagText(match[1]).trim();
    if (title) {
      headingsWithPosition.push({ title, position: match.index });
    }
  }

  headingsWithPosition.sort((a, b) => a.position - b.position);

  const headings: IHeadingItem[] = [];
  for (const item of headingsWithPosition) {
    const slug = generateHeadingSlug(item.title);

    if (seenTitles.has(slug)) {
      continue;
    }
    seenTitles.add(slug);

    headings.push({
      id: slug,
      title: item.title,
      level: 1,
    });
  }

  return headings;
}

export function extractH2Headings(content: string): IHeadingItem[] {
  if (!content || typeof content !== "string") {
    return [];
  }

  interface IHeadingWithPosition {
    title: string;
    position: number;
  }

  const headingsWithPosition: IHeadingWithPosition[] = [];
  const seenTitles = new Set<string>();

  const markdownH2Regex = /^##(?!#)\s+(.+)$/gm;
  let match;
  while ((match = markdownH2Regex.exec(content)) !== null) {
    const title = extractHeadingText(match[0]);
    if (title) {
      headingsWithPosition.push({ title, position: match.index });
    }
  }

  const htmlH2Regex = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
  while ((match = htmlH2Regex.exec(content)) !== null) {
    const title = extractHtmlTagText(match[1]).trim();
    if (title) {
      headingsWithPosition.push({ title, position: match.index });
    }
  }

  headingsWithPosition.sort((a, b) => a.position - b.position);

  const headings: IHeadingItem[] = [];
  for (const item of headingsWithPosition) {
    const slug = generateHeadingSlug(item.title);

    if (seenTitles.has(slug)) {
      continue;
    }
    seenTitles.add(slug);

    headings.push({
      id: slug,
      title: item.title,
      level: 2,
    });
  }

  return headings;
}

export function extractHeadings(content: string, levels: number[] = [1]): IHeadingItem[] {
  if (!content || typeof content !== "string") {
    return [];
  }

  const headings: IHeadingItem[] = [];
  const lines = content.split("\n");
  const seenSlugs = new Map<string, number>();

  for (const line of lines) {
    const trimmedLine = line.trim();
    const headingMatch = trimmedLine.match(/^(#{1,6})\s*(.+)$/);

    if (headingMatch) {
      const level = headingMatch[1].length;

      if (levels.includes(level)) {
        const title = extractHeadingText(trimmedLine);
        let slug = generateHeadingSlug(title);

        const currentCount = seenSlugs.get(slug);
        if (currentCount !== undefined) {
          const newCount = currentCount + 1;
          seenSlugs.set(slug, newCount);
          slug = `${slug}-${newCount}`;
        } else {
          seenSlugs.set(slug, 0);
        }

        if (title && slug) {
          headings.push({
            id: slug,
            title,
            level,
          });
        }
      }
    }
  }

  return headings;
}

export function createHeadingRenderers({ t, onCopyLink, copiedSectionId }: IHeadingRenderersProps): Pick<NonNullable<MarkdownPreviewProps["components"]>, "h1" | "h2" | "h3" | "h4" | "h5"> {
  return {
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>): ReactNode => (
      <h1 {...props} className={`font-size-h1 font-weight-semibold ${props.className || ""}`}>{children}</h1>
    ),
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>): ReactNode => {
      const headingText = extractTextFromChildren(children);
      const id = generateHeadingSlug(headingText);
      const copyLinkAriaLabel = headingText
        ? `${t("contentPage.copyLink")} ${headingText}`
        : t("contentPage.copyLink");

      if (onCopyLink && id) {
        return (
          <div id={id} className={styles.headingSection}>
            <div className={styles.headingHeader}>
              <button
                type="button"
                className={styles.copyLinkButton}
                onClick={(): void => onCopyLink(id)}
                aria-label={copyLinkAriaLabel}
                title={copiedSectionId === id ? t("contentPage.linkCopied") : t("contentPage.copyLink")}
              >
                <LinkMultipleFilled className={styles.linkIcon} />
              </button>
              <h2 {...props} className={`font-size-h2 font-weight-semibold ${props.className || ""}`}>{children}</h2>
            </div>
          </div>
        );
      }

      return (
        <h2
          {...props}
          id={id || undefined}
          className={`font-size-h2 font-weight-semibold ${props.className || ""}`}
        >
          {children}
        </h2>
      );
    },
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>): ReactNode => (
      <h3 {...props} className={`font-size-h3 font-weight-semibold ${props.className || ""}`}>{children}</h3>
    ),
    h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>): ReactNode => (
      <h4 {...props} className={`font-size-h4 font-weight-semibold ${props.className || ""}`}>{children}</h4>
    ),
    h5: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>): ReactNode => (
      <h5 {...props} className={`font-size-h5 font-weight-semibold ${props.className || ""}`}>{children}</h5>
    ),
  };
}

